import { gql } from '@apollo/client';
import { insuranceFragment, hotelFragment } from 'store/fragments';

export const booking = `
  salesChannelCode
  additionalDetails {
    value
    key
    itemRef
  }
  outboundExtras {
    ...product
  }
  inboundExtras {
    ...product
  }
  provisionalOfflineFees: fees(provisional: true) {
    code
    price
    cancelled
  }
  isLoyalty
  totalPriceToBePaid
  agent {
    contractCode
  }
  outboundOrigin {
    uic
    name
  }
  outboundDestination {
    uic
    name
  }
  inboundOrigin {
    uic
    name
  }
  inboundDestination {
    uic
    name
  }
  productFamily
  productFamilyGroup
  isDOBRequired
  isPackage
  isGroupBooking
  nonExchangedOutbound : outbound(hideExchanged: true) {
    id
  }
  nonExchangedInbound : inbound(hideExchanged: true) {
    id
  }
  hotels {
    ...CompleteHotel
  }
  insurances {
    ...CompleteInsurance
  }
  notes {
    uuid
    content
    author
    createdAt
  }
  fees {
    name
    code
    price
    provisional
    currency
    itemRef
    cancelled
  }
  payments {
    id
    uuid
    refId
    currency
    currencyRate
    reference
    externalReference
    refundAvailable
    provider
    method
    amount
    availableRefundAmount
    transactionTimestamp
    refundProcessTimestamp
    paymentStatus
    createdRevision
    isPayout
    isBalance
    isSuccessful
    action @client
    display @client
  }
  discounts: vouchers(class: DISCOUNT) {
    code
    alias
    class
    value
    currency
    paymentId
    isActive
  }
  travelPassProducts {
    travelDate
    startDate
    endDate
    name
    code
    price
    currency
    itemRef
    type
    passenger {
      id
      firstName
      lastName
    }
    cancelled
    provisional
  }
  refunds @client {
    id
    refId
    currency
    currencyRate
    reference
    externalReference
    refundAvailable
    provider
    method
    amount
    transactionTimestamp
    refundProcessTimestamp
    paymentStatus
    action @client
    display @client
  }
  provisionalPayments : payments(onlyProvisional: true) {
    id
    currency
    method
    amount
  }
  amountDue {
    amount
    currency
  }
  reference
  status
  displayStatus @client
  arePassengersEditable @client
  allBookingItems @client {
    ...product
    status
  }
  expiryTimestamp
  isInAfterSales
  passengers {
    ...passenger
  }
  customer {
    firstName
    lastName
    email
    companyName
    phoneNumber
    address {
      street
      postalCode
      city
      countryCode
    }
  }
  currency
  productFamilies {
    code
    name
    familyGroup
  }
  outboundTotalPrice
  inboundTotalPrice
  totalPrice
  outbound {
    ...leg
  }
  inbound {
    ...leg
  }
  eurostarOutbound @client {
    ...leg
  }
  eurostarInbound @client {
    ...leg
  }
  greenspeedOutbound @client {
    ...leg
  }
  greenspeedInbound @client {
    ...leg
  }
  onHold {
    depositAmount
  }
  isMigratedMulti
  isMultiCurrency
  extraOnlyParentBookingReference
  extraOnlyBookingReference
  createTimestamp
  agent {
    organization
    office
    username
  }
  pendingCancellationLegs {
    ...leg
  }
  pendingCreationLegs {
    ...leg
  }
  externalBookingReferences {
    source
    externalId
  }
`;
export const leg = `
  fragment leg on Leg {
    ...legFields
    addOnLeg {
      ...legFields
    }
  }

  fragment legFields on Leg {
    cancelled
    isMultiInventory
    direction
    serviceIdentifier
    trainNumber
    id
    ticketDocuments {
      pin
    }
    classOfService {
      code
      name
    }
    extraProducts {
      ...product
    }
    origin {
      name
      uic
      shortCode
      timezone
      countryCode
      isPseudoStation
    }
    destination {
      name
      uic
      shortCode
      countryCode
      isPseudoStation
    }
    timing {
      isoDate
      date
      departs
      arrives
    }
    fees {
      name
      code
      price
      provisional
      currency
      itemRef
    }
    canBeUpgraded
    products {
      ...product
    }
    activeBookingItems @client {
      ...product
    }

    bookingItems @client {
      ...product
      mealCode
      status
      isSeatEditable
      isMealEditable
    }

    temporarilyCancelledBookingItems @client {
      ...product
      mealCode
      status
    }

    seats {
      coach
      number
      inventoryClass
      itemRef
      passenger {
        id
        type
      }
    }
    meals {
      code
      passenger {
        id
      }
      price
      name
    }
    productSummaries @client {
      name
      passengerType
      code
      total
      quantity
      price
      currency
    }
    refundSummaries @client {
      name
      passengerType
      code
      total
      quantity
      price
    }
    serviceType {
      code
    }
  }
`;

export const passenger = `
  fragment passenger on Passenger {
    id
    uuid
    type
    firstName
    lastName
    email
    phoneNumber
    birthDate
    loyaltyNumber
    disabilityType
  }
`;

export const product = `
  fragment product on Product {
    uuid
    origin {
      uic
      name
    }
    destination {
      uic
      name
    }
    itemRef
    associatedItemRefs
    passengerId
    isAFareAddOn
    isCheckedIn
    isRebookOFEActive
    loyaltyPoints
    price
    currency
    tariffDetails {
      tariffCode
      bucketCode
    }
    currency
    code
    inventoryClass
    name
    cancelled
    provisional
    canBeRebooked
    canBeCancelled
    canBeRefunded
    canChangeSeat
    canChangeMeal
    afterSalesRules {
      active
      validUntil
      override
      price
      type
      currentActive
    }
    passenger {
      ...passenger
    }
    productFamily {
      code
      name
      familyGroup
    }
    type
    additionalDetails {
      key
      value
    }
  }
`;

export default gql`
  mutation updateBookingExtras($currency: String!, $bookingReference: String!, $segments: [InputSegment]) {
    updateBookingExtras(currency: $currency, bookingReference: $bookingReference, segments: $segments) {
      ${booking}
    }
  }

  ${insuranceFragment}

  ${hotelFragment}

  ${leg}

  ${passenger}

  ${product}
`;
